import { Check } from 'lucide-react'
import {
  responseModeNotes,
  responseModes,
} from '@/features/agents-marketing/data/agentsContent'
import { FlowLane } from '@/features/home/components/FlowLane'
import { SectionHeading } from '@/features/home/components/SectionHeading'
import { Reveal } from '@/shared/components/Reveal'
import { useSpotlight } from '@/shared/hooks/useSpotlight'

export function ResponseModesSection() {
  const onPointerMove = useSpotlight()

  return (
    <section className="relative w-full px-6 py-24 md:py-36">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <SectionHeading
            eyebrow="Response modes"
            title="Decide who writes the reply."
            lede="Every specialist has a response mode. It changes what the user gets back, not how the coordinator asks for help."
          />
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-4 md:grid-cols-2">
          {responseModes.map((card, i) => {
            const delegate = card.mode === 'delegate'
            return (
              <Reveal key={card.mode} delay={i * 0.08}>
                <article
                  onPointerMove={onPointerMove}
                  className="surface-bevel spotlight flex h-full flex-col rounded-[1.5rem] p-7"
                >
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-title font-semibold tracking-[-0.02em] text-[var(--color-text-primary)]">
                      {card.label}
                    </h3>
                    {delegate ? null : (
                      <span className="eyebrow text-[11px] text-primary">Default</span>
                    )}
                  </div>
                  <p className="mt-2 text-[var(--color-text-secondary)]">
                    {card.summary}
                  </p>

                  <FlowLane steps={card.steps} className="mt-6" />

                  <p className="mt-6 text-body-sm leading-relaxed text-[var(--color-text-secondary)]">
                    {card.detail}
                  </p>

                  <dl className="mt-6 flex-1 space-y-4 border-t border-[var(--color-border)] pt-6 text-body-sm">
                    <div>
                      <dt className="eyebrow text-[11px]">
                        Prompt
                      </dt>
                      <dd className="mt-1.5 font-mono text-[var(--color-text-primary)]">
                        &ldquo;{card.example.prompt}&rdquo;
                      </dd>
                    </div>
                    <div>
                      <dt className="eyebrow text-[11px]">
                        What comes back
                      </dt>
                      <dd className="mt-1.5 leading-relaxed text-[var(--color-text-secondary)]">
                        {card.example.outcome}
                      </dd>
                    </div>
                  </dl>
                </article>
              </Reveal>
            )
          })}
        </div>

        <Reveal delay={0.15}>
          <ul className="mt-10 flex flex-col gap-2 text-body-sm text-[var(--color-text-tertiary)] md:flex-row md:flex-wrap md:gap-x-8">
            {responseModeNotes.map((note) => (
              <li key={note} className="flex items-start gap-2">
                <Check
                  aria-hidden
                  className="mt-0.5 size-4 shrink-0 text-primary"
                  strokeWidth={1.75}
                />
                {note}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  )
}
